import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map, Search, Copy, ExternalLink, BookOpen } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

/* ============================================================
 * 網站地圖（/admin/sitemap）— 全站頁面一頁導覽
 * 資料為靜態快照：新增頁面時同步更新本表（維護說明見 docs/manuals/README.md）
 * ============================================================ */

interface SiteMapEntry {
  path: string;
  name: string;
  desc: string;
  /** /t/ 分享碼（手冊類專用：複製 hkspicyvalley.com/t/{code}） */
  share?: string;
  /** 對應 Markdown 手冊（部署於 /manuals/） */
  manualHref?: string;
  /** 僅 super_admin 可用（名稱顯示標註） */
  super_only?: boolean;
}

interface SiteMapSection {
  id: string;
  title: string;
  badge: string;
  desc: string;
  entries: SiteMapEntry[];
}

const SITE_MAP: SiteMapSection[] = [
  {
    id: 'customer',
    title: '顧客端 · 公開頁面',
    badge: '免登入',
    desc: '顧客直接瀏覽；關鍵動作（落單確認、領優惠）經 WhatsApp 完成。',
    entries: [
      { path: '/', name: '首頁', desc: '品牌入口：Hero、人氣辣物輪換、故事、禮品碼 CTA——文案圖片全經 CMS 可改' },
      { path: '/story', name: '品牌故事（香港製造）', desc: '製程圖庫＋點擊放大 Lightbox；頂部導航顯示「品牌故事」' },
      { path: '/media-coverage', name: '媒體報導', desc: '報導彙整頁' },
      { path: '/store', name: '商店', desc: '產品格＋三層篩選＋火熱抽籤＋套餐推介區；右側購物車摘要卡＋抽屜' },
      { path: '/products/:slug', name: '產品詳情', desc: '圖廊、辣度徽章、適合菜式、數量、「開心分享」（連結自動帶轉介碼 ?r=）' },
      { path: '/p/:id', name: '產品短連結', desc: '3 位短碼（開心分享產生）；舊數字 id 兼容' },
      { path: '/cart', name: '購物車（獨立頁）', desc: '⚠ 孤兒頁：目前全站無入口連結，主流程走商店頁抽屜直去結帳' },
      { path: '/checkout', name: '結帳', desc: '聯絡資料＋送貨地址＋付款方式（FPS/PayMe）＋訂單摘要＋優惠碼' },
      { path: '/checkout/confirm/:orderNum', name: 'WhatsApp 確認落單', desc: '5 秒自動開 WhatsApp＋預填訊息＋QR 後備；每 4 秒輪詢至確認' },
      { path: '/o/:orderNum', name: '訂單已確認（付款資訊頁）', desc: 'FPS/PayMe 收款資料＋上傳憑證入口；狀態徽章固定「待付款」屬正常設計' },
      { path: '/order/:orderNum', name: '訂單查詢', desc: '訂單號＋電話驗證；顯示 8 態狀態（等待確認→已完成）' },
      { path: '/payment-proof/:orderNum', name: '上傳付款憑證', desc: 'JPG/PNG 最大 5MB；上傳後訂單轉「待確認收款」' },
      { path: '/offers', name: '食肆優惠', desc: '地區 TAB＋餐廳搜尋＋分頁（每頁 15）；領取經 WhatsApp 關鍵字 OFFER:{key}' },
      { path: '/c/:key', name: '優惠短連結', desc: '/offers/:key 同源；直開該優惠彈窗，自動帶訪客 ?r=' },
      { path: '/redeem/:code', name: '優惠兌換頁', desc: '顧客模式＝大 QR（3 秒自動更新）；?m=1＝店員核銷模式（輸 4 位驗證碼）' },
      { path: '/me/:code', name: '客戶優惠中心', desc: '顧客專屬 standing link：優惠券列表＋商家通知開關＋全部拒收（PDPO）' },
      { path: '/godfather/:key', name: '推廣活動落地頁', desc: '每場活動專屬；填表領試吃裝＋折扣碼；防刷一號碼一 IP；/godfather 無 key 用默認活動' },
      { path: '/gf-share/:id', name: '★新 分享任務提交頁', desc: '參與者貼 FB/IG 分享帖連結→提交→即時 WhatsApp 確認（任務閉環終端）' },
      { path: '/wanla/:code', name: '★新 辣友卡換領頁', desc: '會員月度換領碼；手機出示／店員掃 dashboard QR 直達；30 日冷卻自助核銷' },
      { path: '/chilifans', name: '★新 辣友會行動計劃（內部簡報）', desc: 'GTM v2.0 逐幕動畫簡報——會員生命週期 12 幕＋三階段引爆＋30 日清單＋指標；robots Disallow、無 popup label；分享俾持份者用直連' },
      { path: '/terms', name: '條款及細則', desc: '公開靜態頁' },
      { path: '/privacy', name: '隱私條款', desc: 'PDPO 對應；優惠領取／拒收流程引用' },
    ],
  },
  {
    id: 'portals',
    title: '協作門戶 · 專屬連結進入',
    badge: '連結即入',
    desc: '外部夥伴免帳號——持有連結（WhatsApp 派發）即入自己門戶。',
    entries: [
      { path: '/merchant/center', name: '商家中心', desc: '?t= 長期連結或電話 OTP；首次協議閘→總覽／優惠漏斗／推廣工具（QR 海報）／客源洞察；只見聚合數字不見客人電話' },
      { path: '/merchant/submit-offer', name: '商家提交優惠（公開表單）', desc: '免登入提交；批准後自動以聯絡電話開通商家中心＋發 standing link' },
      { path: '/merchant/agreement', name: '商家服務協議', desc: 'PDPO 資料條款＋費用＋終止（v1.0）；協議事務熱線 852 6232 2466' },
      { path: '/supplier/portal', name: '供貨商門戶', desc: '?token= 連結（48 小時或長期）；提交產品（最多 3 圖）＋我的記錄；審批結果 WhatsApp 通知' },
      { path: '/ref/d/:token', name: '推薦夥伴 Dashboard', desc: '永久 token；雙 TAB（🎁辣友卡會員／💰推薦賺回贈——消費 10% 回贈，自用變相九折）＋專屬連結＋流量漏斗＋成交紀錄' },
    ],
  },
  {
    id: 'shortlinks',
    title: '短連結家族 · 302 轉址',
    badge: '隱藏結構',
    desc: '對外分享用——不暴露內部路徑與邏輯；全部為 file-based Pages Functions。',
    entries: [
      { path: '/t/:code', name: '培訓手冊分享碼 ★新', desc: '→ /manuals/#{角色}；4 位英數碼（86vr/3as6/j9mw/78n2/y3qe/hf6a——無歧義字符、口傳錯一位唔會落錯角色）；無效碼落總覽。分享手冊俾第三方一律用呢條' },
      { path: '/gf/:code', name: '推廣活動碼', desc: '→ /godfather/{key}；4 位碼（建活動自動生成）；UTM 透傳；無效碼落默認活動' },
      { path: '/c/:key', name: '優惠碼', desc: '→ 優惠詳情彈窗；商家推廣工具／分享鏈用' },
      { path: '/p/:id', name: '產品碼', desc: '→ 產品詳情；3 位短碼（開心分享）' },
      { path: '/sg/:slug', name: '推薦人碼', desc: '→ /ref/d/{token}；夥伴 Dashboard 短碼' },
      { path: '/m/:token', name: '商家碼', desc: '→ /merchant/center?t=；對爬蟲回 OG meta（縮圖＋個人化 title）' },
      { path: '/s/:token', name: '供貨商碼', desc: '→ /supplier/portal?token=；6 位 Base62' },
      { path: '/o/:orderNum', name: '訂單碼', desc: '→ 訂單付款資訊頁（WhatsApp 確認後自動跳轉目標）' },
    ],
  },
  {
    id: 'admin-daily',
    title: '管理後台 · 每日營運',
    badge: '帳號登入',
    desc: '/admin 帳號登入（角色 super_admin／admin；24 小時時效）。',
    entries: [
      { path: '/admin', name: '管理員登入', desc: '帳號密碼；admin 級選單自動隱藏無權限項目' },
      { path: '/admin/dashboard', name: '儀表板', desc: '8 KPI＋近 7 天趨勢＋付款狀態分佈（待審核＝等你確認收款）＋庫存預警＋最近訂單' },
      { path: '/admin/conversations', name: 'WhatsApp 對話', desc: '客戶對話查讀＋回覆＋📎 圖片附件（JPEG 壓縮）＋入站圖片直出＋Webhook 日誌' },
      { path: '/admin/orders', name: '訂單管理', desc: '★雙 TAB：待處理隊列（未收款∪未標記完成，最舊優先）／全部訂單＋六篩選（含訂單狀態）＋CSV；八態膠囊' },
      { path: '/admin/orders/:id', name: '訂單詳情', desc: '★雙 TAB：訂單詳情（核對憑證→一鍵確認付款→標記完成；KPay 退款）／客戶對話（全寬＋📎 發圖）；複製訂單資料' },
    ],
  },
  {
    id: 'admin-customers',
    title: '管理後台 · 顧客與旅程',
    badge: '帳號登入',
    desc: '客戶資產與自動化：清單、標籤、旅程、禮品。',
    entries: [
      { path: '/admin/customers', name: '客戶清單', desc: '訂單客＋推廣 lead＋導入聯繫人統一主檔；標籤／拒收／導入／CSV' },
      { path: '/admin/customer-journeys', name: '客戶旅程', desc: '自動化工作流設計：觸發→條件→延遲→動作（發 WhatsApp／打標籤／送禮品）' },
      { path: '/admin/auto-tags', name: '自動標籤', desc: '行為條件自動打標籤＋綁 WhatsApp 模板觸發；客戶／推薦人／手動三 TAB' },
      { path: '/admin/gift-catalog', name: '禮品目錄', desc: '可經 Workflow 贈送的隨單禮品定義' },
      { path: '/admin/pending-gifts', name: '待送禮品', desc: '掛在客戶名下的禮品狀態流轉（待送出→已隨單→已交付）' },
      { path: '/admin/pending-jobs', name: '待發訊息', desc: '已排程未發的 workflow 訊息；客人要求可取消排程' },
    ],
  },
  {
    id: 'admin-collab',
    title: '管理後台 · 協作平台',
    badge: '帳號登入',
    desc: '商家／供貨商／推薦網絡／優惠——外部合作全部在這組。',
    entries: [
      { path: '/admin/offers', name: '食肆優惠', desc: '四 TAB：優惠管理（驗證碼/重領開關）／索取記錄（解鎖/手動核銷）／商家提交審批／成效報表（漏斗＋分享）' },
      { path: '/admin/merchants', name: '商家中心管理', desc: '登記商家／發放與輪換長期連結（舊連結即時作廢）／指派優惠／推廣成效（談判籌碼）' },
      { path: '/admin/supplier', name: '供貨商中心', desc: '生成 token 連結（48h／長期）＋產品審批（批准可調最終售價；拒絕必填原因）' },
      { path: '/admin/referrals', name: '推薦網絡', desc: '夥伴列表（等級 Bronze→Platinum 自動算）＋流量統計＋「同步遺漏佣金」' },
      { path: '/admin/referrals/:phone', name: '推薦人詳情', desc: '五 TAB（推薦/訂單/獎勵/流量）＋傭金對銷（付款參考會 WhatsApp 發夥伴）＋鏈路圖' },
    ],
  },
  {
    id: 'admin-store',
    title: '管理後台 · 商品與行銷',
    badge: '帳號登入',
    desc: '產品、套餐、活動、優惠碼、廣播、彈窗。',
    entries: [
      { path: '/admin/products', name: '產品管理', desc: '三層篩選＋CRUD＋CSV 匯入匯出＋類別管理；庫存 <15 入低庫存警報' },
      { path: '/admin/package-configs', name: '套餐配置', desc: '必含組件＋自選組（每組任選 N）；套餐價服務端權威（前台傳價無效）' },
      { path: '/admin/godfather', name: '推廣活動', desc: '三 TAB：活動管理（短連結複製）／領取名單（郵寄狀態＋A4 14 上 Label 列印）／成效（漏斗＋CAC-LTV）' },
      { path: '/admin/wanla', name: '辣友卡會員', desc: '持續性年費會員：T+30/31 個人化換領碼推送／參與店鋪 O2O 流量統計／本月設定（餐廳＋聖品）' },
      { path: '/admin/syslog', name: '系統日誌', desc: '全域錯誤／警告追蹤（error/warn/info 篩選＋搜尋；保留 30 日）——出錯秒速定位', super_only: true },
      { path: '/admin/promo-codes', name: '優惠碼（super_admin）', desc: '折扣碼三類：固定金額／百分比／免運費；次數上限＋有效期' },
      { path: '/admin/campaigns', name: '推廣廣播', desc: '四步精靈（受眾 tags／全部／指定名單→內容→預覽→發送）；圖片＋按鈕＋{name} 個人化；節奏每活動可調＋批次中停；自動過濾拒收名單' },
      { path: '/admin/popup', name: '進站推薦彈窗', desc: '顯示頁面清單自動派生自路由表（新頁面加 label 即出現）；賣點輪播；成效報表' },
    ],
  },
  {
    id: 'admin-data',
    title: '管理後台 · 數據分析',
    badge: '帳號登入',
    desc: '看數做決策：成效、蹤跡、AI 洞見。',
    entries: [
      { path: '/admin/analytics', name: '成效總覽', desc: 'KPI 六卡＋轉化漏斗＋顧客分層＋推薦網絡＋WhatsApp 營運＋優惠成效' },
      { path: '/admin/journeys', name: '客戶蹤跡', desc: '群體矩陣（來源×結果＋漏斗＋放棄熱點，每晚 01:00 彙總）＋電話級時間線（web 旅程橋接訂單）' },
      { path: '/admin/insights', name: '每日洞見', desc: '每晚 01:00 簡報（DoW 對比＋洞見＋AI 即用建議）；動作打卡＋3 日後自動歸因；效果排行；大腦設置（提示詞 CMS 化）' },
    ],
  },
  {
    id: 'admin-system',
    title: '管理後台 · 內容與系統',
    badge: '帳號登入',
    desc: '前台內容、媒體、WhatsApp 模板、帳號、日誌。',
    entries: [
      { path: '/admin/cms', name: '網頁內容管理（CMS）', desc: '前台文案／圖片／設定即改即生效（首頁 Hero、品牌故事、WhatsApp 號碼等）' },
      { path: '/admin/media-library', name: '媒體庫', desc: '圖片上傳＋分類＋批次管理；WebP（網頁用）／JPG（WhatsApp 用，WebP 會被當貼圖）' },
      { path: '/admin/default-responses', name: '預設回應', desc: '29 個 WhatsApp 模板統一編輯（訂單/優惠/夥伴/Workflow）；儲存即生效' },
      { path: '/admin/settings', name: '基本設置（super_admin）', desc: '收款資料（FPS/戶口）＋免運門檻＋危險刪除區；WhatsApp 連線狀態／測試發送／QR 重連' },
      { path: '/admin/system', name: '★新 系統架構（super_admin）', desc: '技術層全局：部署拓撲 4 層＋D1/KV/R2/CloudWAPI＋閘道健康＋24h 錯誤＋Worker 排程＋短連結家族——權威文檔 ARCHITECTURE＋附錄', super_only: true },
      { path: '/admin/users', name: '用戶管理', desc: '同事帳號（角色分級）＋重置密碼＋登入資料 WhatsApp 發送' },
      { path: '/admin/audit-logs', name: '操作日誌（super_admin）', desc: '後台操作審計（誰改了什麼）' },
    ],
  },
  {
    id: 'manuals',
    title: '培訓與支援 · 手冊分享',
    badge: '免登入',
    desc: '持份者自學體系（規則 82）：分幕網站＋五本手冊。「分享 /t/x」複製 4 位英數短連結，直接 WhatsApp 分享俾第三方（商家/供貨商/夥伴/客人），不會暴露 /manuals/ 內部結構與角色明文。',
    entries: [
      { path: '/manuals/', name: '分幕培訓網站（總覽）', desc: '6 劇本 36 幕：走馬燈選角色→一幕一幕學；鍵盤←→/空白鍵/Esc 導覽', share: '86vr' },
      { path: '/manuals/#customer', name: '顧客培訓劇本', desc: '買嘢七幕：逛→落單→WhatsApp 確認→付款→查單→領優惠→禮遇', share: '3as6', manualHref: '/manuals/customer-guide.md' },
      { path: '/manuals/#admin', name: '管理員培訓劇本', desc: '開工三件事→核款→完成→優惠→傭金→推廣→洞見', share: 'j9mw', manualHref: '/manuals/admin-guide.md' },
      { path: '/manuals/#merchant', name: '商家培訓劇本', desc: '連結即入→總覽→店員核銷三步→推廣工具→提交優惠', share: '78n2', manualHref: '/manuals/merchant-guide.md' },
      { path: '/manuals/#supplier', name: '供貨商培訓劇本', desc: '專屬連結→提交產品→看審批進度', share: 'y3qe', manualHref: '/manuals/supplier-guide.md' },
      { path: '/manuals/#referrer', name: '推薦夥伴培訓劇本', desc: '成為之友→Dashboard→分享姿勢→成交紀錄→兌現回贈', share: 'hf6a', manualHref: '/manuals/referrer-guide.md' },
    ],
  },
];

// 持份者支援卡：每個角色「對方點接入」一句話（分發俾第三方時嘅心理台階）
const ROLE_ONBOARD: Record<string, { emoji: string; onboard: string }> = {
  '86vr': { emoji: '🗺️', onboard: '任何持份者第一步——開總覽自己揀角色劇本' },
  '3as6': { emoji: '🛍️', onboard: '客人／潛在客人——WhatsApp 直接分享購物全流程指南' },
  'j9mw': { emoji: '🧑‍💼', onboard: '新同事入職——開工三件事到進階營運全包' },
  '78n2': { emoji: '🏪', onboard: '合作餐廳——連結即入自己門戶＋店員核銷三分鐘學識' },
  'y3qe': { emoji: '📦', onboard: '貨源夥伴——專屬連結提交產品、追審批進度' },
  'hf6a': { emoji: '🤝', onboard: '辣谷之友——Dashboard 睇回贈、正確分享姿勢' },
};

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

const AdminSitemap: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>(SITE_MAP[0]?.id || 'customer');

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin');
    }
  }, [navigate]);

  const q = query.trim().toLowerCase();

  const sections = useMemo(() => {
    if (!q) return SITE_MAP;
    return SITE_MAP.map((s) => ({
      ...s,
      entries: s.entries.filter(
        (e) =>
          e.path.toLowerCase().includes(q) ||
          e.name.toLowerCase().includes(q) ||
          e.desc.toLowerCase().includes(q)
      ),
    })).filter((s) => s.entries.length > 0);
  }, [q]);

  // TAB 模式：無搜尋時只顯示當前分區；搜尋時跨全部分區出結果
  const visibleSections = q ? sections : SITE_MAP.filter((s) => s.id === activeSection);

  const total = useMemo(() => SITE_MAP.reduce((n, s) => n + s.entries.length, 0), []);
  const shown = sections.reduce((n, s) => n + s.entries.length, 0);

  const handleCopy = async (code: string) => {
    const ok = await copyText(`${window.location.origin}/t/${code}`);
    setCopied(ok ? code : 'FAIL');
    window.setTimeout(() => setCopied(null), 1800);
  };

  return (
    <AdminLayout currentPage="sitemap">
      <div className="space-y-6">
        {/* 標題與說明 */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800 dark:text-white">
              <Map size={26} className="text-red-600" /> 網站地圖
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              全站 {total} 個頁面／連結一頁導覽——邊個用、點入去、做什麼。新增頁面時同步更新本表。
            </p>
          </div>
          <a
            href="/manuals/"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700"
          >
            <BookOpen size={16} /> 培訓網站 /manuals/
          </a>
        </div>

        {/* 搜尋 */}
        <div className="relative">
          <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜尋路徑、頁面名或功能（例如：優惠、兌換、廣播）"
            className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-800 focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>
        {/* TAB 分區欄（可橫向滾動；搜尋時自動切換為跨分區結果模式） */}
        <div className="flex gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700">
          {SITE_MAP.map((s) => {
            const active = !q && s.id === activeSection;
            const short = s.title.split('·')[0].trim();
            return (
              <button
                key={s.id}
                onClick={() => { setActiveSection(s.id); setQuery(''); }}
                className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {short}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${active ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                  {s.entries.length}
                </span>
              </button>
            );
          })}
        </div>

        {q && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            「{query}」跨全部分區找到 {shown} 個頁面
          </p>
        )}

        {/* 當前分區內容（搜尋模式＝全部命中分區） */}
        {visibleSections.map((section) => (
          <section key={section.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <header className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <h2 className="text-base font-bold text-gray-800 dark:text-white">{section.title}</h2>
              <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                {section.badge}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{section.entries.length} 頁</span>
            </header>
            <p className="border-b border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
              {section.desc}
            </p>
            {section.id === 'manuals' ? (
              /* 持份者支援：角色卡 grid——對方點接入＋三個動作一卡搞掂 */
              <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3">
                {section.entries.map((e) => {
                  const role = e.share ? ROLE_ONBOARD[e.share] : undefined;
                  return (
                    <div key={e.path + e.name} className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 transition hover:border-red-300 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:hover:border-red-800">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{role?.emoji || '📘'}</span>
                          <span className="font-bold text-gray-800 dark:text-white">{e.name.replace('劇本', '').replace('（總覽）', '')}</span>
                        </div>
                        {e.share && (
                          <button
                            onClick={() => handleCopy(e.share!)}
                            className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-bold transition ${
                              copied === e.share
                                ? 'bg-green-600 text-white'
                                : copied === 'FAIL'
                                  ? 'bg-red-600 text-white'
                                  : 'bg-red-600 text-white hover:bg-red-700'
                            }`}
                            title={`複製分享短連結 /t/${e.share}`}
                          >
                            <Copy size={12} /> {copied === e.share ? '已複製 ✓' : `/t/${e.share}`}
                          </button>
                        )}
                      </div>
                      <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-400">{e.desc}</p>
                      {role && (
                        <p className="rounded-lg bg-amber-50 px-2.5 py-1.5 text-xs text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                          接入：{role.onboard}
                        </p>
                      )}
                      <div className="mt-auto flex gap-2">
                        <a
                          href={e.path}
                          target="_blank"
                          rel="noopener"
                          className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs font-medium text-gray-600 hover:border-red-500 hover:text-red-600 dark:border-gray-600 dark:text-gray-300"
                        >
                          <ExternalLink size={12} /> 分幕劇本
                        </a>
                        {e.manualHref && (
                          <a
                            href={e.manualHref}
                            target="_blank"
                            rel="noopener"
                            className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs font-medium text-gray-600 hover:border-red-500 hover:text-red-600 dark:border-gray-600 dark:text-gray-300"
                          >
                            <BookOpen size={12} /> 完整手冊
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    <th className="px-4 py-2 font-medium">路徑</th>
                    <th className="px-4 py-2 font-medium">頁面</th>
                    <th className="px-4 py-2 font-medium">說明</th>
                    <th className="px-4 py-2 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {section.entries.map((e) => {
                    const canOpen = !e.path.includes(':');
                    return (
                      <tr key={e.path + e.name} className="border-b border-gray-100 align-top last:border-0 dark:border-gray-700/60">
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                            {e.path}
                          </code>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-white">{e.name}</td>
                        <td className="max-w-[440px] px-4 py-2.5 text-gray-600 dark:text-gray-400">{e.desc}</td>
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <div className="flex items-center gap-2">
                            {canOpen && (
                              <a
                                href={e.path}
                                target="_blank"
                                rel="noopener"
                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-red-500 hover:text-red-600 dark:border-gray-600 dark:text-gray-300"
                                title="新分頁開啟"
                              >
                                <ExternalLink size={12} /> 開啟
                              </a>
                            )}
                            {e.share && (
                              <button
                                onClick={() => handleCopy(e.share!)}
                                className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold transition ${
                                  copied === e.share
                                    ? 'bg-green-600 text-white'
                                    : copied === 'FAIL'
                                      ? 'bg-red-600 text-white'
                                      : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                                title={`複製分享短連結 /t/${e.share}`}
                              >
                                <Copy size={12} /> {copied === e.share ? '已複製 ✓' : `分享 /t/${e.share}`}
                              </button>
                            )}
                            {e.manualHref && (
                              <a
                                href={e.manualHref}
                                target="_blank"
                                rel="noopener"
                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-red-500 hover:text-red-600 dark:border-gray-600 dark:text-gray-300"
                                title="完整 Markdown 手冊"
                              >
                                <BookOpen size={12} /> 手冊
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </section>
        ))}

        {q && sections.length === 0 && (
          <p className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            沒有符合「{query}」的頁面——試其他關鍵字（例如：訂單、商家、報表）
          </p>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSitemap;
